import { Injectable } from '@angular/core';
import { Action, Selector, SelectorOptions, State, StateContext } from '@ngxs/store';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ApiService, ChannelSetDeviceSettingsInterface } from '@pe/checkout/api';
import {
  AccordionPanelInterface,
  CheckoutStateParamsInterface,
  FlowInterface,
  CheckoutSettingsInterface,
  PaymentMethodEnum,
  PaymentOptionInterface,
  ConnectionSettingsInterface,
  PaymentMethodWithIssuerInterface,
} from '@pe/checkout/types';

import { FlowState } from '../flow';
import { ParamsState } from '../params';
import { initialPaymentState, PaymentState, PaymentStateModel } from '../payment';
import { SettingsState } from '../settings';
import { SplitState, SplitStateModel } from '../split';
import { StepsState } from '../steps';

import {
  ClearState,
  GetConnectionSettings,
  HidePayment,
  SetChannelSetSettings,
  SetLockedUi,
  SetPaymentComplete,
  SetPrevAction,
} from './checkout.actions';


export interface CheckoutStateModel {
  flow: FlowInterface;
  params: CheckoutStateParamsInterface;
  settings?: CheckoutSettingsInterface;
  steps: AccordionPanelInterface[];
  paymentComplete: boolean;
  lockedUi: boolean;
  hiddenPayments: PaymentMethodWithIssuerInterface[];
  payment: PaymentStateModel;
  channelSetSettings: ChannelSetDeviceSettingsInterface;
  prevAction: unknown;
  split?: SplitStateModel;
  connectionSettings: ConnectionSettingsInterface[];
}

const initialState: CheckoutStateModel = {
  flow: null,
  params: {},
  steps: null,
  hiddenPayments: null,
  lockedUi: null,
  paymentComplete: null,
  payment: initialPaymentState,
  channelSetSettings: null,
  prevAction: null,
  split: null,
  connectionSettings: null,
};

const IGNORED_PAYMENTS: {
  [payment in PaymentMethodEnum]?: boolean;
} = {
  santander_factoring_de: true,
  santander_pos_factoring_de: true,
  santander_invoice_de: true,
  santander_pos_invoice_de: true,
};

@State<CheckoutStateModel>({
  name: 'checkout',
  children: [
    FlowState,
    ParamsState,
    SettingsState,
    StepsState,
    PaymentState,
    SplitState,
  ],
  defaults: initialState,
})
@SelectorOptions({
  injectContainerState: false,
  suppressErrors: false,
})
@Injectable()
export class CheckoutState {
  constructor(
    private readonly apiService: ApiService,
  ) {}

  @Selector() static paymentComplete(state: CheckoutStateModel) {
    return state.paymentComplete;
  }

  @Selector() static lockedUi(state: CheckoutStateModel) {
    return state.lockedUi;
  }

  @Selector() static hiddenPayments(state: CheckoutStateModel) {
    return state.hiddenPayments;
  }

  @Selector() static channelSetSettings(state: CheckoutStateModel) {
    return state.channelSetSettings;
  }

  @Selector() static prevAction(state: CheckoutStateModel) {
    return state.prevAction;
  }

  @Selector() static connectionSettings(state: CheckoutStateModel) {
    return state.connectionSettings;
  }

  @Selector() static connectionSettingsMap(state: CheckoutStateModel): Map<string, ConnectionSettingsInterface> {
    const connectionSettingsMap = new Map();
    state.connectionSettings.forEach((connectionSetting) => {
      connectionSettingsMap.set(connectionSetting.connectionId, connectionSetting);
    });

    return connectionSettingsMap;
  }

  @Selector([FlowState.flow, CheckoutState.connectionSettings]) static activeConnectionSetting(
    { connectionId }: FlowInterface,
    connectionSettings: ConnectionSettingsInterface[],
  ) {
    return connectionSettings.find(item => item.connectionId === connectionId);
  }

  @Selector([
    FlowState.flow,
    SettingsState.settings,
    ParamsState.params,
    CheckoutState.hiddenPayments,
  ]) static paymentOptions(
    flow: FlowInterface,
    settings: CheckoutSettingsInterface,
    params: CheckoutStateParamsInterface,
    hiddenPayments: PaymentMethodWithIssuerInterface[],
  ) {

    return this.filterPaymentMethods(
      flow,
      settings,
      params,
      hiddenPayments,
    );
  }

  private static filterPaymentMethods(
    flow: FlowInterface,
    settings: CheckoutSettingsInterface,
    params: CheckoutStateParamsInterface,
    hiddenPayments: PaymentMethodWithIssuerInterface[]
  ): PaymentOptionInterface[] {
    const options: PaymentOptionInterface[] = flow.paymentOptions;
    let result: PaymentOptionInterface[] = settings
      ? (options || []).filter(option => (settings.paymentMethods || []).indexOf(option.paymentMethod) >= 0)
      : options;

    if (hiddenPayments) {
      result = result.filter(payment => hiddenPayments.some(
        item => item.paymentMethod !== payment.paymentMethod
          || (item.paymentIssuer && item.paymentIssuer !== payment.paymentIssuer)
      ));
    }

    if (!(window as any).ApplePaySession) { // If Apple Pay is not available if hide it
      result = result.filter(option => option.paymentMethod !== PaymentMethodEnum.APPLE_PAY);
    }

    if (flow.connectionId) {
      result = (params.forcePaymentOnly || params.forceChoosePaymentOnlyAndSubmit)
        ? result.filter(o => o.connections.find(c => c.id === flow.connectionId))
        : result;
    }

    if (params.forceSinglePaymentMethodOnly) {
      result = result.filter(o => o.paymentMethod === params.forceSinglePaymentMethodOnly);
    }

    const procutsSubs: {[key: string]: string[]} = {};
    (flow.cart || []).forEach(cart => procutsSubs[cart.productId] = []);
    Object.entries(procutsSubs || {}).forEach(([k, p]) => p.length === 0 ? delete procutsSubs[k] : null);

    // For Billing Subscription we filter only if any of item has Billing Subscription (plan)
    const values = Object.values(procutsSubs || {});
    if (values.length) {
      const intersections = values[0].reduce((acc, value, index) => {
          values.every(str => value === str[index]) && acc.push(value);

          return acc;
      }, []);

      result = result.filter(p => intersections.indexOf(p.paymentMethod) >= 0);
    }

    return result.filter(o => !IGNORED_PAYMENTS[o.paymentMethod]);
  }

  @Action(ClearState)
  clearState({ patchState }: StateContext<CheckoutStateModel>) {
    patchState(initialState);
  }

  @Action(SetLockedUi)
  setLockedUi(
    { patchState }: StateContext<CheckoutStateModel>,
    { lockedUi }: SetLockedUi,
  ) {
    patchState({ lockedUi });
  }

  @Action(SetPaymentComplete)
  setPaymentComplete(
    { patchState }: StateContext<CheckoutStateModel>,
    { payload }: SetPaymentComplete,
  ) {
    patchState({
      paymentComplete: payload ?? true,
    });
  }

  @Action(HidePayment)
  hidePayment(
    { getState, patchState }: StateContext<CheckoutStateModel>,
    { payload }: HidePayment,
  ) {
    const { hiddenPayments } = getState();

    patchState({ hiddenPayments: (hiddenPayments || []).concat(payload) });
  }

  @Action(SetChannelSetSettings)
  setChannelSetSettings(
    { patchState }: StateContext<CheckoutStateModel>,
    { channelSetSettings }: SetChannelSetSettings,
  ) {
    patchState({ channelSetSettings });
  }

  @Action(SetPrevAction)
  setPrevAction(
    { patchState }: StateContext<CheckoutStateModel>,
    { action }: SetPrevAction,
  ) {
    patchState({ prevAction: action });
  }

  @Action(GetConnectionSettings)
  getConnectionSettings(
    { getState, patchState }: StateContext<CheckoutStateModel>,
    { connectionIds }: GetConnectionSettings,
  ) {
    const { flow: { businessId }, connectionSettings } = getState();

    if (connectionSettings && connectionSettings.length) {
      return of(connectionSettings);
    }

    return this.apiService.getConnectionSettings(businessId, connectionIds).pipe(
      tap((connectionSettings: ConnectionSettingsInterface[]) => {
        patchState({
          connectionSettings,
        });
      }),
    );
  }
}
